import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";
import LanguageSwitcher from "../../translation/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import maleAvatar from "../../assets/maleAvatar.webp";
import logo from "../../assets/logo.png";
import styles from "./PublicLanding.module.css";
import { youtubeUrlToEmbedSrc } from "../../utils/youtubeEmbedUrl";
import { isApprovedTeacher } from "../../utils/teacherStatus";
import Contact from "../contact/Contact";
import success from "../../assets/success.webp";
import future from "../../assets/future.webp";
const PublicLanding = () => {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [teacherList, setTeacherList] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    const load = async () => {
      try {
        const ref = collection(db, "teachers");
        const snap = await getDocs(ref);
        const list = snap.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }))
          .filter(isApprovedTeacher);
        setTeacherList(list);
      } catch (e) {
        console.error(e);
        setFetchError(e.message || "Failed to load tutors");
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  if (authLoading) {
    return (
      <div className={styles.loading}>
        {t("PublicLanding.loading")}
      </div>
    );
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/" className={styles.logoLink} aria-label={t("PublicLanding.brand")}>
          <img src={logo} className={styles.logo} alt="" />
        </Link>

        <button
          type="button"
          className={`${styles.menuToggle} ${menuOpen ? styles.menuToggleOpen : ""}`}
          aria-expanded={menuOpen}
          aria-controls="landing-nav"
          aria-label={menuOpen ? t("PublicLanding.closeMenu") : t("PublicLanding.openMenu")}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className={styles.menuToggleBar} />
          <span className={styles.menuToggleBar} />
          <span className={styles.menuToggleBar} />
        </button>

        <nav
          id="landing-nav"
          className={`${styles.headerNav} ${menuOpen ? styles.headerNavOpen : ""}`}
          aria-label={t("PublicLanding.mainNav")}
        >
          <a
            className={styles.headerTeachersLink}
            href="#teachers-section"
            onClick={closeMenu}
          >
            {t("PublicLanding.teachersHeading")}
          </a>
          <a
            className={styles.headerTeachersLink}
            href="#contact-section"
            onClick={closeMenu}
          >
            {t("PublicLanding.navContact")}
          </a>
          <LanguageSwitcher
            variant="light"
            fullWidth
            className={styles.navLanguageWrapper}
            selectClassName={styles.navLanguageSelect}
          />
          <Link className={styles.loginLink} to="/login" onClick={closeMenu}>
            {t("PublicLanding.login")}
          </Link>
        </nav>

        {menuOpen && (
          <button
            type="button"
            className={styles.menuBackdrop}
            aria-label={t("PublicLanding.closeMenu")}
            onClick={closeMenu}
          />
        )}
      </header>

      <div className={styles.heroContainer}>
        <div className={styles.heroLayout}>
          <div className={styles.heroVisual}>
            <img
              src={success}
              alt=""
              className={styles.successImage}
            />
            <img
              src={future}
              alt=""
              className={styles.futureImage}
            />
          </div>
          <div className={styles.heroContent}>
            <section className={styles.hero}>
              <h1 id="app-pitch-heading" className={styles.pitchTitle}>
                {t("PublicLanding.appPitchTitle")}
              </h1>
              <h1 className={styles.heroTitle}>{t("PublicLanding.heroTitle")}</h1>
              <p className={styles.heroSubtitle}>{t("PublicLanding.heroSubtitle")}</p>
            </section>

            <div className={styles.marketing}>
              <section className={styles.marketingSection} aria-labelledby="how-it-works-heading">
                <h2 id="how-it-works-heading" className={styles.sectionTitle}>
                  {t("PublicLanding.howItWorksTitle")}
                </h2>
                <div className={styles.steps} role="list">
                  <div className={`${styles.stepBox} ${styles.stepBox1}`} role="listitem">
                    <h3 className={styles.stepHeading}>{t("PublicLanding.step1Title")}</h3>
                    <p className={styles.stepText}>{t("PublicLanding.step1Body")}</p>
                  </div>
                  <div className={`${styles.stepBox} ${styles.stepBox2}`} role="listitem">
                    <h3 className={styles.stepHeading}>{t("PublicLanding.step2Title")}</h3>
                    <p className={styles.stepText}>{t("PublicLanding.step2Body")}</p>
                  </div>
                  <div className={`${styles.stepBox} ${styles.stepBox3}`} role="listitem">
                    <h3 className={styles.stepHeading}>{t("PublicLanding.step3Title")}</h3>
                    <p className={styles.stepText}>{t("PublicLanding.step3Body")}</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {fetchError && (
        <p className={styles.error} role="alert">
          {fetchError}
        </p>
      )}

      <h2 id="teachers-section" className={styles.teachersHeading}>
        {t("PublicLanding.teachersHeading")}
      </h2>
      <div className={styles.grid}>
        {teacherList.map((teacher) => {
          const id = teacher.id || teacher.userId;
          if (!id) return null;
          const { firstName, lastName, profileImage, introVideo } = teacher;
          const name = `${firstName || ""} ${lastName || ""}`.trim() || id;
          const bookTo = `/login?redirect=${encodeURIComponent(`/teachers/${id}`)}`;
          return (
            <article className={styles.card} key={id}>
              <img
                className={styles.avatar}
                src={profileImage || maleAvatar}
                alt=""
              />
              <h2 className={styles.name}>{name}</h2>
              <div className={styles.videoWrap}>
                {introVideo ? (
                  <iframe
                    title={name}
                    className={styles.iframe}
                    src={youtubeUrlToEmbedSrc(introVideo)}
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <p className={styles.noVideo}>{t("PublicLanding.noVideo")}</p>
                )}
              </div>
              <Link className={styles.bookBtn} to={bookTo}>
                {t("PublicLanding.bookThisTeacher")}
              </Link>
            </article>
          );
        })}
      </div>
      <section
        id="contact-section"
        className={styles.contactSection}
        aria-label={t("PublicLanding.navContact")}
      >
        <Contact />
      </section>

      <footer className={styles.siteFooter}>
        <p className={styles.siteFooterText}>{t("PublicLanding.footerDisclaimer")}</p>
      </footer>
    </div>
  );
};

export default PublicLanding;
